export type ProductModuleId = 'core' | 'clinical' | 'laboratory' | 'radiology' | 'pharmacy' | 'patient_portal'

export type ProductSkuId =
  | 'full_hospital'
  | 'full_hospital_portal'
  | 'diagnostic'
  | 'diagnostic_portal'
  | 'lab_only'
  | 'radiology_only'
  | 'pharmacy_only'

export type ModuleTheme = {
  primary: string
  brand: string
  accent: string
  shell: string
}

/** Full hospital chrome — olive (unchanged). */
export const DEFAULT_MODULE_THEME: ModuleTheme = {
  primary: '#A79A46',
  brand: '#A79A46',
  accent: '#81A16F',
  shell: '#243D20'
}

/** Standalone pharmacy chrome — soft violet (buttons) + deep violet shell (sidebar). */
export const PHARMACY_MODULE_THEME: ModuleTheme = {
  primary: '#7C3AED',
  brand: '#7C3AED',
  accent: '#A78BFA',
  shell: '#4C1D95'
}

/** Lab-only chrome — Access PHS Digital Aqua. */
export const LAB_ONLY_THEME: ModuleTheme = {
  primary: '#24D1CE',
  brand: '#24D1CE',
  accent: '#0EC5E2',
  shell: '#0A4F52'
}

/** Radiology-only chrome — Access PHS Deep Authority + Soft Coral accent. */
export const RADIOLOGY_ONLY_THEME: ModuleTheme = {
  primary: '#0D1E66',
  brand: '#0D1E66',
  accent: '#ED9387',
  shell: '#071236'
}

/** Diagnostic (lab + radiology) chrome — Access PHS Soft Coral + Innovation Gold accent. */
export const DIAGNOSTIC_THEME: ModuleTheme = {
  primary: '#ED9387',
  brand: '#ED9387',
  accent: '#EACD12',
  shell: '#5C2E2A'
}

/** Resolve app chrome from product SKU. Full hospital keeps olive; pharmacy keeps purple. */
export const themeForSku = (sku: string | undefined | null): ModuleTheme => {
  switch (sku) {
    case 'pharmacy_only':
      return PHARMACY_MODULE_THEME
    case 'lab_only':
      return LAB_ONLY_THEME
    case 'radiology_only':
      return RADIOLOGY_ONLY_THEME
    case 'diagnostic':
    case 'diagnostic_portal':
      return DIAGNOSTIC_THEME
    default:
      return DEFAULT_MODULE_THEME
  }
}

export const isDefaultHospitalSku = (sku: string | undefined | null): boolean =>
  !sku || sku === 'full_hospital' || sku === 'full_hospital_portal'

export type ProductModuleDef = {
  id: ProductModuleId
  label: string
  theme: ModuleTheme
  /** Sidebar / route permission keys owned by this module */
  sidebarKeys: string[]
  /** Role resource permission keys (config capabilities.resources) owned by this module */
  resourceKeys: string[]
  /** Path prefixes that activate this module's accent */
  pathPrefixes: string[]
}

export const PRODUCT_MODULES: Record<ProductModuleId, ProductModuleDef> = {
  core: {
    id: 'core',
    label: 'Core',
    theme: { ...DEFAULT_MODULE_THEME },
    sidebarKeys: [
      'staff',
      'role',
      'department',
      'facility',
      'unit',
      'units',
      'shift',
      'schedule',
      'patient',
      'appointment',
      'audit log',
      'archives',
      'registration template',
      'hmos',
      'document template',
      'charge item',
      'billing',
      'reports',
      'message'
    ],
    resourceKeys: [
      'history',
      'patient',
      'staff',
      'role',
      'department',
      'equipment',
      'shift',
      'schedule',
      'appointment',
      'hmo',
      'hmo_plan',
      'hmo_tariff',
      'unit',
      'document_template',
      'archive_folder',
      'archive_document',
      'charge_item',
      'invoice',
      'invoice_item',
      'code',
      'room',
      'specialty',
      'facility',
      'registration_form_template',
      'registration_submission'
    ],
    pathPrefixes: ['/reports']
  },
  clinical: {
    id: 'clinical',
    label: 'Clinical',
    theme: { ...DEFAULT_MODULE_THEME },
    sidebarKeys: [
      'encounter',
      'assigned patient',
      'doctorswaitinglist',
      'nurseswaitinglist',
      'receptionistswaitinglist',
      'receptionistsintake',
      'nursesintake',
      'mar',
      'bed request',
      'pending admits',
      'antenatal',
      'nurse_requisition',
      'procedure'
    ],
    resourceKeys: [
      'encounter',
      'allergy',
      'vital_sign',
      'family_history',
      'medical_history',
      'surgical_history',
      'social_history',
      'medication',
      'note',
      'procedure',
      'medication_administration',
      'bed',
      'bed_request',
      'admission_order',
      'consent_form',
      'addendum',
      'intake',
      'antenatal'
    ],
    pathPrefixes: ['/patient-chart', '/mar', '/waitlist', '/intake', '/encounters', '/assigned', '/anc', '/admission']
  },
  laboratory: {
    id: 'laboratory',
    label: 'Laboratory',
    theme: { ...LAB_ONLY_THEME },
    sidebarKeys: ['laboratory', 'lab_test', 'laboratory document template'],
    resourceKeys: ['lab_test', 'lab_sample', 'lab_result', 'lab_order', 'lab_test_template'],
    pathPrefixes: ['/laboratory']
  },
  radiology: {
    id: 'radiology',
    label: 'Radiology',
    theme: { ...RADIOLOGY_ONLY_THEME },
    sidebarKeys: ['radiology', 'imaging', 'imaging document template'],
    resourceKeys: ['imaging', 'imaging_result', 'imaging_order', 'imaging_test_template'],
    pathPrefixes: ['/radiology']
  },
  pharmacy: {
    id: 'pharmacy',
    label: 'Pharmacy',
    theme: { ...PHARMACY_MODULE_THEME },
    sidebarKeys: [
      'med order',
      'drug inventory',
      'dispensings',
      'pharmacist_requisition',
      'pharmacy reports',
      'drug',
      'supplier',
      'location'
    ],
    resourceKeys: [
      'pharmaceutical',
      'supplier',
      'drug',
      'order',
      'product',
      'product_inventory',
      'inventory_location',
      'dispensing',
      'requisition'
    ],
    pathPrefixes: ['/pharmacy-workspace', '/med-order', '/drug', '/dispense', '/product', '/pharmacy-reports']
  },
  patient_portal: {
    id: 'patient_portal',
    label: 'Patient Portal',
    theme: { ...DEFAULT_MODULE_THEME },
    sidebarKeys: [],
    resourceKeys: [],
    pathPrefixes: ['/patient-portal', '/patient/']
  }
}

export const PRODUCT_SKUS: Record<ProductSkuId, { label: string; modules: ProductModuleId[] }> = {
  full_hospital: {
    label: 'Full Hospital',
    modules: ['core', 'clinical', 'laboratory', 'radiology', 'pharmacy']
  },
  full_hospital_portal: {
    label: 'Full Hospital + Portal',
    modules: ['core', 'clinical', 'laboratory', 'radiology', 'pharmacy', 'patient_portal']
  },
  diagnostic: {
    label: 'Diagnostic (Lab + Radiology)',
    modules: ['core', 'laboratory', 'radiology']
  },
  diagnostic_portal: {
    label: 'Diagnostic + Portal',
    modules: ['core', 'laboratory', 'radiology', 'patient_portal']
  },
  lab_only: {
    label: 'Lab only',
    modules: ['core', 'laboratory']
  },
  radiology_only: {
    label: 'Radiology only',
    modules: ['core', 'radiology']
  },
  pharmacy_only: {
    label: 'Pharmacy only',
    modules: ['core', 'pharmacy']
  }
}

const SIDEBAR_KEY_TO_MODULE = Object.values(PRODUCT_MODULES).reduce(
  (acc, mod) => {
    mod.sidebarKeys.forEach(key => {
      acc[key.toLowerCase()] = mod.id
    })
    return acc
  },
  {} as Record<string, ProductModuleId>
)

const RESOURCE_KEY_TO_MODULE = Object.values(PRODUCT_MODULES).reduce(
  (acc, mod) => {
    mod.resourceKeys.forEach(key => {
      acc[key.toLowerCase()] = mod.id
    })
    return acc
  },
  {} as Record<string, ProductModuleId>
)

export const moduleForSidebarKey = (key: string | string[]): ProductModuleId | null => {
  const keys = Array.isArray(key) ? key : [key]
  for (const k of keys) {
    const mod = SIDEBAR_KEY_TO_MODULE[k.toLowerCase()]
    if (mod) return mod
  }
  return null
}

export const moduleForResourceKey = (key: string | string[]): ProductModuleId | null => {
  const keys = Array.isArray(key) ? key : [key]
  for (const k of keys) {
    const mod = RESOURCE_KEY_TO_MODULE[k.toLowerCase()]
    if (mod) return mod
  }
  return null
}

export const resolveActiveModule = (pathname: string, enabled: ProductModuleId[]): ProductModuleId => {
  const enabledSet = new Set(enabled)
  const ranked = (Object.values(PRODUCT_MODULES) as ProductModuleDef[])
    .filter(m => m.id !== 'core' && enabledSet.has(m.id))
    .flatMap(m => m.pathPrefixes.map(prefix => ({ id: m.id, prefix })))
    .sort((a, b) => b.prefix.length - a.prefix.length)

  const hit = ranked.find(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`) || pathname.startsWith(prefix)
  )
  if (hit) return hit.id

  const nonCore = enabled.filter(id => id !== 'core' && id !== 'patient_portal')
  if (nonCore.length === 1) return nonCore[0]
  return 'core'
}

export const modulesForSku = (sku: string): ProductModuleId[] => {
  const preset = PRODUCT_SKUS[sku as ProductSkuId]
  return preset ? [...preset.modules] : [...PRODUCT_SKUS.full_hospital_portal.modules]
}

/** Seeded / default role names → owning product module. Unknown roles are not gated. */
export const ROLE_TO_MODULE: Record<string, ProductModuleId> = {
  administrator: 'core',
  receptionist: 'clinical',
  doctor: 'clinical',
  nurse: 'clinical',
  pharmacist: 'pharmacy',
  laboratory_technician: 'laboratory',
  radiologist: 'radiology',
  patient: 'patient_portal'
}

export const moduleForRoleName = (roleName: string | undefined | null): ProductModuleId | null => {
  if (!roleName) return null
  const normalized = roleName
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
  return ROLE_TO_MODULE[normalized] ?? null
}
